import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { API } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling.js";
import { ALERTS } from "../../locales/components/namespaces.js";
import useUserAction from "../user/useUserAction";
import useUserData from "../user/useUserData";

/*
 * Λήψη δεδομένων για ιστορικό αλλαγών συγκεκριμένου entity για τα επιλεγμένα πεδία.
 * Φέρνει όλα τα δεδομένα που βρίσκει για τις συγκεκριμένους παραμέτρους.
 * Σε περίπτωση που θέλουμε να τα εμφανίσουμε σε πίνακα με server side φίλτρα και Paging, τότε δεν μπορεί να χρησιμοποιηθεί.
 * Χρησιμοποιούμε το useMuiGridServerSide και πρέπει να φτιαχτεί αντίστοιχη κλήση στο backend με τη συνάρτηση requestGridData που τα χειρίζεται στον server.
 *
 * Στην απάντηση τα property που έρχονται ως Mark + το όνομα πεδίου i.e.'MarkAssignee' με τιμές 0 ή 1, σημαίνουν οτι
 * η συγκεκριμένη τιμή άλλαξε και μπορούν να χρησιμοποιηθούν για παράδειγμα για χρωματισμό του κελιού που δείχνει την αλλαγμένη τιμή.
 * Το property InitRow το αγνοούμε, αφορά το backend.
 * Υπάρχουν πάντα τα properties Entity, EntityId, ModifiedAt, LastModifiedBy, LastModified
 */

const useAuditing = ({
						 entity, // i.e prjTasks
						 entityId, // i.e το συγκεκριμένο id του Task
						 entityFields = [], // Τα πεδία του πίνακα της οντότητας για τα οποία θέλουμε το ιστορικό τους i.e [{FieldName: 'EstimDueDt', UserField: 0}, {FieldName: 'AssigneeId', UserField: 1}]. Το property UserField ενημερώνει αν το πεδίο έχει σύνδεση με τον ksrUsers.
						 onSuccess = () => {
						 }, // Προαιρετική χρήση για τρέξιμο custom λογικής στην απάντηση
						 onFailure = () => {
						 }, // Προαιρετική χρήση για τρέξιμο custom λογικής στην αποτυχία απάντησης,
						 mapResponseData // Προαιρετική χρήστη για να κάνεις map τα δεδομένα της απάντησης.
					 }) => {
	
	const { t } = useTranslation(ALERTS)
	const [loading, setLoading] = useState(false)
	const [entityHistory, setEntityHistory] = useState([])
	const {token, isLoggedIn} = useUserData()
	const {userLogout} = useUserAction()
	const getEntityHistory = useCallback((current = true, otherParams) => {
		if (entity && entityId) {
			setLoading(true)
			axios({
				method: 'post',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				url: `${API}/en/ude/data/auditing`,
				data: {
					Entity: entity,
					EntityId: entityId,
					Fields: entityFields,
					...otherParams
				}
			}).then((response) => {
				if (current && response.data?.data) {
					const responseData = response.data.data.map(i => ({
						...i,
						...(i.id && { idSentFromData: i.id }), // Σε περίπτωση που τα δεδομένα στέλνουν κάποιο Property με όνομα id, να μη γίνει override από το id που φτιάχνουμε για το grid από κάτω.
						id: uuidv4() // unique identifier for use in data grid
					}))
					const mappedData = mapResponseData ? mapResponseData([...responseData]) : [...responseData]
					setEntityHistory(mappedData)
					onSuccess({ response, entity, entityId, entityFields, mappedData })
				}
				setLoading(false)
			}).catch((error) => {
				setLoading(false)
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!'), {
					variant: 'error',
					autoHideDuration: 3000,
				}))
				onFailure({ error, entity, entityId, entityFields })
				logoutUnauthorizedUser(error, isLoggedIn, userLogout)
			})
		}
	}, [entity, mapResponseData, onFailure, onSuccess, t, isLoggedIn, userLogout])
	
	useEffect(() => {
		let current = true
		getEntityHistory(current)
		return () => {
			current = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	
	return {
		loading,
		entityHistory,
		getEntityHistory
	}
}

export default useAuditing